import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { User, Upload, X, Eye, EyeOff, Save, Calendar, Copy, RefreshCw, Check } from 'lucide-react';

export default function PerfilPage() {
  const { user, login } = useAuth();
  const [tab, setTab] = useState('datos');
  const [form, setForm] = useState({
    nombre: user?.nombre || '',
    apellido: user?.apellido || '',
    telefono: user?.telefono || '',
    cargo: user?.cargo || '',
    direccion: user?.direccion || '',
  });
  const [firma, setFirma] = useState(user?.firma || null);
  const [saving, setSaving] = useState(false);
  const [passForm, setPassForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [showPass, setShowPass] = useState(false);
  const firmaRef = useRef();

  const [feedUrl, setFeedUrl] = useState(null);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadFeedUrl = async () => {
    setLoadingFeed(true);
    try {
      const res = await api.get('/calendar/feed-url');
      setFeedUrl(res.data.url);
    } catch { toast.error('Error al generar el enlace'); }
    finally { setLoadingFeed(false); }
  };

  const regenerarFeedUrl = async () => {
    if (!window.confirm('Esto invalida el enlace anterior — si ya lo agregaste a Outlook, tendrás que quitarlo y agregar el nuevo. ¿Continuar?')) return;
    setLoadingFeed(true);
    try {
      const res = await api.post('/calendar/feed-url/regenerate');
      setFeedUrl(res.data.url);
      toast.success('Enlace regenerado');
    } catch { toast.error('Error al regenerar el enlace'); }
    finally { setLoadingFeed(false); }
  };

  const copiarFeedUrl = () => {
    if (!feedUrl) return;
    navigator.clipboard.writeText(feedUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => { if (tab === 'calendario' && !feedUrl) loadFeedUrl(); }, [tab]); // eslint-disable-line

  const f = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleSaveDatos = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (firma !== user?.firma) payload.firma = firma;
      const res = await api.put('/users/profile', payload);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success('Perfil actualizado ');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleFirmaUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Solo se admiten imágenes PNG o JPG'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('La imagen no puede superar 2MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setFirma(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleChangePass = async (e) => {
    e.preventDefault();
    if (passForm.newPassword !== passForm.confirm) { toast.error('Las contraseñas no coinciden'); return; }
    if (passForm.newPassword.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    setSaving(true);
    try {
      await api.put('/users/change-password', { currentPassword: passForm.currentPassword, newPassword: passForm.newPassword });
      toast.success('Contraseña actualizada ');
      setPassForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.error || 'Error al cambiar contraseña'); }
    finally { setSaving(false); }
  };

  const rolLabel = { master: 'Master', admin: 'Admin', gerente_financiera: 'Ger. Financiera', gerente_proyectos: 'Ger. Proyectos', usuario: 'Usuario', tecnico: 'Técnico' }[user?.rol] || 'Usuario';
  const initials = user ? `${user.nombre[0]}${user.apellido[0]}`.toUpperCase() : 'U';

  return (
    <div style={{ maxWidth: 700 }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Mi perfil</h1>
          <p className="page-subtitle">Gestiona tus datos y firma digital</p>
        </div>
      </div>

      {/* Avatar + info rápida */}
      <div className="card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 20 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#7B0D0D', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, flexShrink: 0 }}>
          {initials}
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{user?.nombre} {user?.apellido}</div>
          <div style={{ fontSize: 14, color: '#888', marginTop: 2 }}>{user?.email} · {rolLabel}</div>
          {user?.cargo && <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>{user.cargo}</div>}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'white', padding: 4, borderRadius: 10, width: 'fit-content', boxShadow: 'var(--sombra)' }}>
        {[{ k: 'datos', label: 'Datos personales' }, { k: 'firma', label: 'Firma digital' }, { k: 'pass', label: 'Contraseña' }, { k: 'calendario', label: 'Sincronizar calendario' }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, background: tab === t.k ? '#7B0D0D' : 'transparent', color: tab === t.k ? 'white' : '#666', transition: 'all 0.2s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Datos personales */}
      {tab === 'datos' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 20 }}>Información personal</h3>
          <form onSubmit={handleSaveDatos}>
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-input" required value={form.nombre} onChange={f('nombre')} />
              </div>
              <div className="form-group">
                <label className="form-label">Apellido *</label>
                <input className="form-input" required value={form.apellido} onChange={f('apellido')} />
              </div>
            </div>
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">Cargo</label>
                <input className="form-input" value={form.cargo} onChange={f('cargo')} placeholder="Ej: Asesor comercial" />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input className="form-input" value={form.telefono} onChange={f('telefono')} placeholder="+593..." />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Dirección</label>
              <input className="form-input" value={form.direccion} onChange={f('direccion')} placeholder="Km. 16,5 vía Daule - Ecuador" />
            </div>
            <div style={{ padding: '12px 14px', background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd', fontSize: 13, color: '#0369a1', marginBottom: 16 }}>
              ℹ️ Tu nombre, cargo, dirección y firma aparecerán en las cotizaciones que generes.
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Save size={16} /> {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </form>
        </div>
      )}

      {/* Firma digital */}
      {tab === 'firma' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 8 }}>Firma digital</h3>
          <p style={{ fontSize: 14, color: '#888', marginBottom: 20 }}>
            Sube una imagen PNG de tu firma con fondo transparente o blanco. Esta firma aparecerá automáticamente en todas tus cotizaciones.
          </p>

          {firma ? (
            <div>
              <div style={{ padding: 16, background: '#f9f9f9', borderRadius: 10, border: '2px dashed #e5e5e5', marginBottom: 16, textAlign: 'center' }}>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Vista previa de tu firma:</p>
                <img src={firma} alt="Firma" style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary" onClick={() => firmaRef.current.click()}>
                  <Upload size={16} /> Cambiar firma
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => setFirma(null)}>
                  <X size={16} /> Eliminar
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => firmaRef.current.click()}
              style={{ border: '2px dashed #ccc', borderRadius: 12, padding: 40, textAlign: 'center', cursor: 'pointer', background: '#fafafa', transition: 'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#7B0D0D'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#ccc'}>
              <Upload size={36} style={{ color: '#ccc', marginBottom: 12 }} />
              <p style={{ color: '#666', fontSize: 14, marginBottom: 4 }}>Haz clic para subir tu firma</p>
              <p style={{ color: '#aaa', fontSize: 12 }}>PNG o JPG · Máximo 2MB · Recomendado: fondo blanco o transparente</p>
            </div>
          )}

          <input ref={firmaRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFirmaUpload} />

          {firma !== (user?.firma || null) && (
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={handleSaveDatos} disabled={saving}>
              <Save size={16} /> {saving ? 'Guardando...' : 'Guardar firma'}
            </button>
          )}
        </div>
      )}

      {/* Cambiar contraseña */}
      {tab === 'pass' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 20 }}>Cambiar contraseña</h3>
          <form onSubmit={handleChangePass}>
            <div className="form-group">
              <label className="form-label">Contraseña actual</label>
              <div style={{ position: 'relative' }}>
                <input className="form-input" type={showPass ? 'text' : 'password'} required style={{ paddingRight: 44 }}
                  value={passForm.currentPassword} onChange={e => setPassForm(p => ({ ...p, currentPassword: e.target.value }))} />
                <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}>
                  {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Nueva contraseña</label>
              <input className="form-input" type="password" required minLength={6}
                value={passForm.newPassword} onChange={e => setPassForm(p => ({ ...p, newPassword: e.target.value }))} placeholder="Mínimo 6 caracteres" />
            </div>
            <div className="form-group">
              <label className="form-label">Confirmar nueva contraseña</label>
              <input className="form-input" type="password" required
                value={passForm.confirm} onChange={e => setPassForm(p => ({ ...p, confirm: e.target.value }))} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Actualizando...' : 'Actualizar contraseña'}
            </button>
          </form>
        </div>
      )}

      {/* Sincronizar calendario */}
      {tab === 'calendario' && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}><Calendar size={17} /> Sincronizar tus citas con Outlook</h3>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 20, lineHeight: 1.6 }}>
            Este enlace es personal e intransferible: trae las citas donde tú eres el asesor o donde te invitaron. Lo agregas una sola vez en Outlook (o Google Calendar) como "calendario desde internet", y desde ahí se actualiza solo — cada vez que se agende, edite o cancele una cita tuya en ODIN, aparece reflejado en tu calendario. Outlook revisa el enlace cada cierto tiempo (no es instantáneo, normalmente entre 15 y 60 minutos — eso lo controla Outlook, no ODIN).
          </p>

          {loadingFeed ? (
            <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner spinner-dark" style={{ width: 26, height: 26, margin: '0 auto' }} /></div>
          ) : feedUrl && (
            <>
              <div className="form-group">
                <label className="form-label">Tu enlace de suscripción</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-input" readOnly value={feedUrl} style={{ fontSize: 12.5, color: '#555' }} onClick={e => e.target.select()} />
                  <button type="button" className="btn btn-secondary" onClick={copiarFeedUrl} style={{ flexShrink: 0 }}>
                    {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
              </div>

              <div style={{ background: '#f8f8f8', borderRadius: 10, padding: '14px 16px', marginTop: 16, fontSize: 13, lineHeight: 1.9 }}>
                <strong style={{ display: 'block', marginBottom: 6 }}>Cómo agregarlo en Outlook (escritorio o web):</strong>
                1. Copia el enlace de arriba.<br />
                2. En Outlook → panel de <strong>Calendario</strong> → botón <strong>Agregar calendario</strong> → <strong>Suscribirse desde la web</strong> (o "Desde Internet").<br />
                3. Pega el enlace, ponle un nombre (ej. "Agenda ODIN") y guarda.<br />
                4. Tus citas de ODIN van a aparecer como un calendario aparte, que puedes mostrar u ocultar junto a tu calendario normal.
              </div>

              <button type="button" className="btn btn-secondary btn-sm" onClick={regenerarFeedUrl} style={{ marginTop: 16 }}>
                <RefreshCw size={14} /> Regenerar enlace
              </button>
              <p style={{ fontSize: 12, color: '#999', marginTop: 8 }}>Regenéralo solo si crees que alguien más obtuvo este enlace — invalida el anterior y hay que volver a agregarlo en Outlook.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
